export type AuthMode =
  | 'login'
  | 'recover'
  | 'reset'
  | 'error'
  | 'dashboard'
  | 'profile'
  | 'collaborators'
  | 'trash'
  | 'spaces'
  | 'permissions'
  | 'rules'
  | 'details'
  | 'settings'

export type LoginFormValues = {
  email: string
  password: string
  rememberMe: boolean
}

export type RecoveryFormValues = {
  email: string
}

export type ResetFormValues = {
  token: string
  password: string
  confirmPassword: string
}

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

export function parseAuthMode(route: string): AuthMode {
  const normalized = route.replace(/^#/, '').replace(/^\/+/, '').toLowerCase()

  if (normalized === 'recover' || normalized === 'forgot-password') {
    return 'recover'
  }

  if (normalized === 'reset' || normalized === 'reset-password') {
    return 'reset'
  }

  if (normalized === 'error' || normalized === 'errors' || normalized === 'auth-error') {
    return 'error'
  }

  if (normalized === 'dashboard' || normalized === 'home') {
    return 'dashboard'
  }

  if (normalized === 'profile' || normalized === 'me') {
    return 'profile'
  }

  if (normalized === 'collaborators' || normalized === 'team') {
    return 'collaborators'
  }

  if (normalized === 'trash' || normalized === 'recycle-bin' || normalized === 'bin') {
    return 'trash'
  }

  if (normalized === 'spaces') {
    return 'spaces'
  }

  if (normalized === 'permissions') {
    return 'permissions'
  }

  if (normalized === 'rules') {
    return 'rules'
  }

  if (normalized === 'details' || normalized === 'detail' || normalized === 'auditoria') {
    return 'details'
  }

  if (normalized === 'settings' || normalized === 'configuracoes' || normalized === 'config') {
    return 'settings'
  }

  return 'login'
}

export function authModeToPath(mode: AuthMode): string {
  if (mode === 'recover') {
    return '/recover'
  }

  if (mode === 'reset') {
    return '/reset'
  }

  if (mode === 'error') {
    return '/error'
  }

  if (mode === 'dashboard') {
    return '/dashboard'
  }

  if (mode === 'profile') {
    return '/profile'
  }

  if (mode === 'collaborators') {
    return '/collaborators'
  }

  if (mode === 'trash') {
    return '/trash'
  }

  if (mode === 'spaces') {
    return '/spaces'
  }

  if (mode === 'permissions') {
    return '/permissions'
  }

  if (mode === 'rules') {
    return '/rules'
  }

  if (mode === 'details') {
    return '/details'
  }

  if (mode === 'settings') {
    return '/settings'
  }

  return '/login'
}

export function authModeToHash(mode: AuthMode): string {
  return `#${authModeToPath(mode).replace(/^\//, '')}`
}

export function normalizeEmail(value: string): string {
  return value.trim().toLowerCase()
}

export function isEmailValid(value: string): boolean {
  return emailPattern.test(normalizeEmail(value))
}

export function validateLoginForm(values: LoginFormValues): string[] {
  const errors: string[] = []

  if (!isEmailValid(values.email)) {
    errors.push('email')
  }

  if (values.password.trim().length < 8) {
    errors.push('password')
  }

  return errors
}

export function validateRecoveryForm(values: RecoveryFormValues): string[] {
  return isEmailValid(values.email) ? [] : ['email']
}

export function validateResetForm(values: ResetFormValues): string[] {
  const errors: string[] = []

  if (values.token.trim().length < 6) {
    errors.push('token')
  }

  if (values.password.trim().length < 8) {
    errors.push('password')
  }

  if (values.confirmPassword.trim().length < 8) {
    errors.push('confirmPassword')
  }

  if (values.password !== values.confirmPassword) {
    errors.push('passwordMismatch')
  }

  return errors
}
